import { useCallback, useEffect, useState } from 'react';

/**
 * Custom hook that tracks whether a specific key is currently pressed
 * This is an alternative to the useKeyPress hook from react-use - it keeps re-rendering the component as long as the key is pressed
 *
 * Handles edge cases like:
 * - Window losing focus (blur event)
 * - Tab becoming hidden (visibilitychange event)
 * - Prevents stuck key states when app is backgrounded
 *
 * @param targetKey - The key to track (e.g., 'Shift', 'Control', 'Alt')
 * @param resetOnScreenshot - MacOS screenshot hotkeys Command + Shift + 3/4/5/6 won't trigger any javascript events,
 *    passing a flag to exclusively reset it
 * @returns boolean indicating if the key is currently pressed
 */
export const useKeyPress = (
  targetKey: string,
  resetOnScreenshot = false
): boolean => {
  const [isPressed, setPressed] = useState(false);

  const downHandler = useCallback(
    ({ key, metaKey, shiftKey }: KeyboardEvent) => {
      // MacOS handles the screenshot key combinations deep in the system window server
      // and it starts with Command + Shift
      if (resetOnScreenshot && metaKey && shiftKey) {
        setPressed(false);
        return;
      }
      if (key === targetKey) {
        setPressed(true);
      }
    },
    [resetOnScreenshot, targetKey]
  );

  const upHandler = useCallback(
    ({ key }: KeyboardEvent) => {
      if (key === targetKey) {
        setPressed(false);
      }
    },
    [targetKey]
  );

  const handleReset = useCallback(() => {
    setPressed(false);
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', downHandler);
    window.addEventListener('keyup', upHandler);
    window.addEventListener('blur', handleReset);
    window.addEventListener('visibilitychange', handleReset);

    return () => {
      window.removeEventListener('keydown', downHandler);
      window.removeEventListener('keyup', upHandler);
      window.removeEventListener('blur', handleReset);
      window.removeEventListener('visibilitychange', handleReset);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return isPressed;
};
